// あそびかたの中身。分類（はじめに・戦い・育成・国盗り・その他）→ 項目。
// 数値はなるべく core の設定から読む（調整しても説明が古くならない）。画面の写真は public/art/help/（画面が変わったら撮り直す）。
// 写真の番号札は写真に対する % の位置。本文の番号つきの列（marks）と同じ順に並べる。
import {
  BATTLE_CONFIG,
  CHARS,
  CHAR_IDS,
  ITEM_BASE_COUNT,
  ITEM_SLOTS,
  ITEM_UNLOCK,
  LATE_BUILDING_TYPES,
  LINE_JUTSU_LV,
  LINE_MAX,
  LINE_OUGI_LV,
  META_CONFIG,
  OMAMORI,
  OMAMORI_SLOTS,
  PRODUCTS,
  SKILL_LINES,
  VILLAGE,
  WAZA_LVS,
  surgeRules,
  surgeTraitTable,
  hardUnlocked,
  type BuildingType,
  type ItemId,
} from '@hyakki/core';
import { state } from '../state';
import { BUILDING_INFO, BUILDING_SPRITES, faceHtml, spriteStyle } from '../assets';
import { esc, fmt, resIcon } from '../ui/dom';
import { icon } from '../ui/icons';

export type HelpCat = 'basic' | 'battle' | 'grow' | 'conquest' | 'other';
export const HELP_CATS: [HelpCat, string, string][] = [
  ['basic', 'はじめに', 'info'],
  ['battle', '戦い', 'sortie'],
  ['grow', '育成', 'enhance'],
  ['conquest', '国盗り', 'castle'],
  ['other', 'その他', 'coin'],
];
export interface HelpTopic {
  id: string;
  cat: HelpCat;
  title: string;
  icon: string;
  body: () => string;
}

// ---------- 部品 ----------
const pct = (v: number) => `${Math.round(v * 1000) / 10}%`;
const p = (s: string) => `<p>${s}</p>`;
const h3 = (s: string) => `<h3>${s}</h3>`;
/** 画面の写真。marks は番号札の位置（写真に対する %）。押すと大きく見られる */
function shot(file: string, alt: string, marks: [number, number][] = [], wide = false) {
  return `<figure class="hp-shot ${wide ? 'wide' : ''}"><button class="hp-zoom" data-zoom="/art/help/${file}.webp" aria-label="${esc(alt)}（拡大）">
    <img src="/art/help/${file}.webp" alt="${esc(alt)}" loading="lazy" decoding="async">
    ${marks.map(([x, y], i) => `<i class="hp-mark" style="left:${x}%;top:${y}%">${i + 1}</i>`).join('')}</button>
    <figcaption>${esc(alt)}</figcaption></figure>`;
}
/** 写真と本文を並べる（PC は横、スマホは縦） */
const row = (fig: string, text: string) => `<div class="hp-row">${fig}<div class="hp-col">${text}</div></div>`;
/** 番号つきの列（写真の番号札と対応） */
const marks = (items: string[]) => `<ol class="hp-steps">${items.map((x) => `<li><span>${x}</span></li>`).join('')}</ol>`;
const steps = marks;
/** 名前と説明の表 */
const table = (rows: [string, string][]) => `<dl class="hp-table">${rows.map(([k, v]) => `<div><dt>${k}</dt><dd>${v}</dd></div>`).join('')}</dl>`;
/** 案内役のひとこと */
const tip = (s: string) => `<div class="hp-tip">${faceHtml('yuzuha', 40)}<p>${s}</p></div>`;
/** その画面へ（戦闘中の札では出さない） */
const goBtn = (screen: string, label: string) => `<button class="btn small hp-go" data-help-go="${screen}">${icon('right')}${esc(label)}</button>`;

// ---------- 数値（設定から） ----------
const B = () => BATTLE_CONFIG;
/** 役の出目の例（設定の名前に添える） */
const ROLE_DICE: Record<string, string> = { pinzoro: '1・1・1', shigoro: '4・5・6', jun: '1・2・3 など' };
/** 役の説明（battle の設定から。倍率を調整しても古くならない） */
const roleText = () =>
  Object.entries(B().dice3.roles)
    .map(([k, r]) => `${r.name}${ROLE_DICE[k] ? `（${ROLE_DICE[k]}）` : ''}は${[r.atk ? `与ダメ+${pct(r.atk)}` : '', r.free ? '建築の陣材0' : ''].filter(Boolean).join('と')}`)
    .join('、');
const M = () => META_CONFIG;
/** 系統の数（4人とも同じ） */
const LINE_COUNT = SKILL_LINES.akari?.length ?? 5;
const ITEM_ORDER: ItemId[] = ['heal', 'power', 'guard', 'swift', 'purify', 'spirit'];

function buildingRows() {
  const start: BuildingType[] = ['yagura', 'sakaku', 'nobori'];
  // 本編クリア後の建物（鉄砲狭間など）は、クリアするまで載せない
  const late = !state.profile || !hardUnlocked(state.profile);
  return (Object.keys(BUILDING_INFO) as BuildingType[]).filter((t) => !(late && LATE_BUILDING_TYPES.includes(t))).map((t): [string, string] => {
    const ref = BUILDING_SPRITES[t];
    const pic = ref ? `<i class="hp-pic" style="${spriteStyle(ref)}" aria-hidden="true"></i>` : '';
    const cost = B().buildings[t]?.cost;
    return [`${pic}${esc(BUILDING_INFO[t].name)}`, `${esc(BUILDING_INFO[t].desc)}<br><small>陣材 ${cost ?? '—'}${start.includes(t) ? '・最初から建てられる' : '・建物図面で解放'}</small>`];
  });
}

function itemRows() {
  const price = M().shop.items;
  return ITEM_ORDER.map((id): [string, string] => {
    const it = B().items[id];
    const lv = ITEM_UNLOCK[id];
    const how = id === 'heal' ? '最初から持っている' : `主Lv${lv}で里の万屋に並ぶ（${fmt(price[id])}銭）`;
    return [`${icon(it.icon)}${esc(it.name)}`, `${esc(it.desc)}<br><small>1戦に${ITEM_BASE_COUNT[id]}個・${how}</small>`];
  });
}

function statusRows() {
  const info = B().status.info;
  return (Object.keys(info) as (keyof typeof info)[]).map((k): [string, string] => [esc(info[k].name), `${esc(info[k].desc)}${info[k].good ? '<small>（良い効果）</small>' : ''}`]);
}

function surgeTraitRows() {
  const t = surgeTraitTable();
  const names = B().surge.stageNames;
  return CHAR_IDS.map((id): [string, string] => {
    const st = t[id] ?? {};
    const line = [2, 3].map((s) => (st[s] ?? []).map((x) => `<b>${names[s - 1]}</b> ${esc(x.name)}：${esc(x.desc)}`).join('')).filter(Boolean).join('<br>');
    return [`${faceHtml(id, 30)}${esc(CHARS[id].name)}`, line];
  });
}

function charRows() {
  return CHAR_IDS.map((id): [string, string] => {
    const c = CHARS[id];
    return [`${faceHtml(id, 34)}${esc(c.name)}`, `<small>${esc(c.race)}・${esc(c.role)}・射程${c.base.range}</small><br>術「${esc(c.skill.name)}」${esc(c.skill.desc)}<br><small>${esc(c.passive)}</small>`];
  });
}

function territoryRows() {
  return Object.values(B().territoryRules).map((r): [string, string] => [esc(r.name), esc(r.desc)]);
}

// ---------- 項目 ----------
export const HELP_TOPICS: HelpTopic[] = [
  // ===== はじめに =====
  {
    id: 'goal',
    cat: 'basic',
    title: 'このゲームの目的',
    icon: 'castle',
    body: () =>
      row(
        shot('map', '国盗りの地図'),
        p('百鬼の国を一つずつ攻め落とし、<b>十の国をすべて制圧</b>するのが目的です。') +
          p('戦いは1人のキャラを出撃させる、すごろくのような陣取りの合戦。<b>賽</b>を振って盤を進み、妖を倒し、建物を建て、<b>敵の本陣</b>を落とせば勝ちです。') +
          p('勝つと主（あるじ）の経験値・銭・装備が手に入ります。育てて、次の国へ。') +
          tip('まずは「出撃」から一戦。わからないことがあれば、いつでもここを開いてください。'),
      ),
  },
  {
    id: 'top',
    cat: 'basic',
    title: 'トップ画面',
    icon: 'home',
    body: () =>
      row(
        shot('top', 'トップ画面', [
          [40, 3],
          [17, 9.5],
          [10, 30],
          [89, 22],
          [12, 80],
          [51, 82],
          [41, 88.5],
          [89, 88],
          [50, 96.5],
        ]),
        marks([
          '持っている資源（銭・霊玉・材木・兵糧）',
          '主Lv',
          '任務・プレゼント・お知らせ・物語',
          'ショップ・おまもりガチャ',
          '里の収益。貯まったら押して受け取り',
          '出撃するキャラ（押して切り替え）',
          '出撃：国盗りの地図へ',
          'オート周回',
          '下のタブ：トップ・編成・出撃・強化・里。それ以外は右上の ≡ メニューから',
        ]),
      ),
  },
  {
    id: 'daily',
    cat: 'basic',
    title: '毎日の遊び方',
    icon: 'clock',
    body: () =>
      steps([
        '<b>ログインボーナス</b>を受け取る（掃討チケットも毎日届きます）',
        '<b>里の収益</b>を受け取って、施設を強化',
        '<b>無料のおまもりガチャ</b>を1回',
        '新しい戦場に<b>出撃</b>して国盗りを進める',
        '勝てない戦場があったら、クリア済みの戦場を<b>掃討</b>・<b>オート周回</b>して育成',
        '<b>任務</b>を確認して報酬を受け取る',
      ]) +
      p(`兵糧は${M().stamina.regenMinutes}分に1ずつ回復します。満タンのまま放っておくと、あふれた分は予備に貯まるので無駄になりません。`) +
      tip('迷ったら、編成の「おすすめ装備」と、スキルの「おすすめ」。それだけでも十分戦えます。'),
  },
  {
    id: 'chars',
    cat: 'basic',
    title: '4人の仲間',
    icon: 'user',
    body: () =>
      p('出撃するのは4人のうち1人。それぞれ得意な戦い方が違います。編成やトップで切り替えられます。') +
      table(charRows()) +
      p('育成（主Lv・強化・図面など）は4人で共通です。装備・お守り・スキルの構成はキャラごとに持ちます。'),
  },

  // ===== 戦い =====
  {
    id: 'battle-screen',
    cat: 'battle',
    title: '戦闘画面の見方',
    icon: 'info',
    body: () =>
      row(
        shot('battle', '戦闘画面', [
          [33, 2.4],
          [50, 7.2],
          [9, 12.2],
          [89, 13],
          [49, 33],
          [30, 66],
          [44, 80],
          [50, 98],
        ]),
        marks([
          '手番の順番。左から順に動きます',
          '本陣のHP（左が自分・右が敵）と、陣地の広さの綱引き。押すと陣地の詳細',
          '妖力のメダリオン。撃破で段階が上がります',
          '式神召喚。押すと技の効果',
          '敵の本陣。HPを0にすれば勝ち',
          '自分のキャラ',
          '操作のパレット。真ん中が賽、まわりに建築・攻撃・術・道具・手番終了',
          '今やること・押したときの結果の予告',
        ]) +
          p('パレットは、押したマス（何も無いところなら自分）の上に開きます。「閉じる」でしまえます。') +
          p('盤のマスや駒を押すと、そのマスの情報（HP・攻撃・状態など）が出ます。右上の ≡ で一時停止・速度・操作の切り替え（盤の上のパレット／下のボタン）・オート・降参。'),
      ),
  },
  {
    id: 'turn',
    cat: 'battle',
    title: '手番の流れ',
    icon: 'dice',
    body: () =>
      row(
        shot('dice', '賽を3つ振る：合計で歩数が決まる'),
        steps([
          `パレットの真ん中の<b>賽</b>を押すと、賽を3つ振る（1〜6）。合計で1〜4（${B().dice3.moveAt[0]}以下は1・${B().dice3.moveAt[2] + 1}以上は4）に、基本の+${B().moveBase}を足したマス数だけ動ける。キャラや陣地の段階でさらに増える`,
          '<b>移動</b>：行き先を押すと、届く範囲で陣地を多く塗れる道で進む。自分の隣から1マスずつ押したり、なぞったりして道筋を描くこともできる。もう一度押すか、右下の「移動」で決定。動かないなら「動かない」',
          '<b>建築</b>（してもしなくても）：手番を使いません',
          '<b>行動</b>を1つ：攻撃・術・手番終了',
        ]),
      ) +
      row(shot('move', '動けるマスが光る。右下で「移動」／「動かない」'), p('賽を振るとパレットは閉じ、行ける範囲が光ります。')) +
      p('「手番終了」は<b>防御</b>を兼ねます。次の自分の手番まで受けるダメージが半分。') +
      p('道具はいつ使っても手番は終わりません（1手番に1回）。') +
      p(`<b>役</b>：${roleText()}。その手番だけ。`) +
      p(`<b>術の賽</b>：術・奥義を放つときも3つ振り、合計が大きいほど威力が上がります。ゾロ目なら${B().dice3.skill.zorome}倍。`),
  },
  {
    id: 'attack',
    cat: 'battle',
    title: '攻撃と術',
    icon: 'attack',
    body: () =>
      row(
        shot('attack', '狙う相手を選ぶと、残りのHPの予測が出る'),
        p('パレットの「攻撃」か「術」を押すと狙える相手が光ります。届く敵をそのまま押しても狙えます。相手を選ぶと、その相手の<b>HPのゲージで予測</b>が出て、倒せるなら「撃破可能」と出ます。確かめてから「攻撃する」（もう一度押す）で放ちます。やめるなら「やめる」。') +
          p('<b>術</b>はキャラごとの必殺技。攻撃より強力ですが、一度使うと<b>数手番の待ち</b>があります。') +
          p('隣から殴った相手が生き残ると反撃されることがあります。範囲や貫通の攻撃には反撃がありません。'),
      ),
  },
  {
    id: 'territory',
    cat: 'battle',
    title: '陣地',
    icon: 'flag',
    body: () =>
      row(
        shot('terr', '陣地の詳細（上のゲージを押すと開く）'),
        p('歩いたマスは自分の色に塗られます。盤のうち自分の色の割合が<b>陣地率</b>。龍脈を持っていると上乗せされます。') +
          p(`陣地率が ${Object.values(B().territoryRules)[0].tiers.map((t) => pct(t.share)).join('・')} を超えるごとに段階が上がり、回復・移動・被ダメなどの効果が付きます。効果は戦場ごとに違います（敵にも同じように働きます）。`) +
          p(`<b>本陣への攻撃の強さ</b>も段階で変わります（段階0〜3で ×${B().coreAssault.join(' / ×')}）。陣地を取らずに本陣へ直行すると、なかなか割れません。`),
      ) +
      h3('自陣の中') +
      p('自分の色のマスで、まわり6マスのうち半分以上が自陣のとき、そこは「自陣の中」。足元に光の輪が出て、戦場のルールの効果（与ダメ・被ダメなど）を受けます。そこから動き出すと移動+1。') +
      table(territoryRows()),
  },
  {
    id: 'build',
    cat: 'battle',
    title: '建築',
    icon: 'build',
    body: () =>
      row(
        shot('build', '建てる建物を選ぶ'),
        p('パレットの「建築」から建物を選び、光ったマスを押すと建ちます。<b>手番を使わない</b>ので、建ててから攻撃もできます（1手番に1回）。') +
          p('材料は<b>陣材</b>。毎手番、陣地の広さと龍脈の数に応じて入り、妖を倒しても入ります。') +
          p('建てられるのは自分の色の空いたマスで、自分のすぐ隣（スズネは2マス先まで）。同時に建っていられる数には上限があります。'),
      ) +
      table(buildingRows()) +
      p('櫓・竹柵・幟以外は、強化の「建物図面」で習得すると建てられます。図面のLvを上げると建物が強くなります。'),
  },
  {
    id: 'surge',
    cat: 'battle',
    title: '妖力と奥義',
    icon: 'sp',
    body: () => {
      const r = surgeRules();
      const names = B().surge.stageNames;
      return (
        row(
          shot('stars', '≡メニュー：星の条件と妖力'),
          p(`戦いの中だけで育つ力が<b>妖力</b>。${names.join('・')}の3段階で、戦闘が終われば元に戻ります。`) +
            p(`敵を倒すと<b>撃破ポイント</b>：妖${r.points.neutral}・敵ヒーロー${r.points.hero}・敵の建物${r.points.building}。累計${r.thresholds[1]}で${names[1]}、${r.thresholds[2]}で${names[2]}。番人や章ボスを倒すと一気に1段上がります。`) +
            p(`段階が上がると攻撃・最大HPが上がり（${names[2]}で+${pct(r.stages[2]?.atk ?? 0.24)}）、HPも回復。倒されると${names[0]}に戻ります。`),
        ) +
        h3('キャラごとの特性') +
        table(surgeTraitRows()) +
        h3('奥義') +
        p(`術の系統をLv${LINE_OUGI_LV}にすると、その術の奥義を覚えます。妖力が${names[2]}のとき、持っていく術が奥義になります。<b>妖力のメダリオンを長押し</b>すると構えに入り、術のボタンが「奥義」に変わります。効果は術ごとに違います（スキルの術の詳細に書いてあります）。放つと妖力は${names[0]}に戻ります。`)
      );
    },
  },
  {
    id: 'win',
    cat: 'battle',
    title: '勝ち負けと星',
    icon: 'star',
    body: () =>
      p('<b>勝ち</b>：敵の本陣のHPを0にする。') +
      p(`<b>負け</b>：自分の本陣が落ちる／自分の手番が${B().turnLimit}回を超える／降参する。`) +
      p('キャラが倒されても、しばらくすると本陣の前（鳥居があればその隣）でHP満タンで復活します。そのあいだ本陣は無防備です。') +
      h3('星（★）の条件') +
      table([
        ['★1', '勝つ'],
        ['★2', '一度も倒されずに勝つ'],
        ['★3', '龍脈をすべて自分の色にして勝つ'],
      ]) +
      p('星を初めて取ると霊玉・材木がもらえます。章ごとの星の合計で「星箱」も開きます。'),
  },
  {
    id: 'board',
    cat: 'battle',
    title: '盤の上のもの',
    icon: 'range',
    body: () =>
      row(
        shot('info', '駒を押すと情報が出る'),
        table([
          ['妖（中立）', '野営地のまわりにいる妖。近づくと戦いになります。倒すと陣材・撃破ポイント。2手番ほど誰もいなければ回復して戻ります'],
          ['龍脈', '妖気が立ちのぼる大岩。通ると自分の色に（妖気の色が変わる）。持っていると陣地率と陣材が増え、★3の条件にもなります'],
          ['宝箱', '止まると陣材と、銭・霊玉・装備のどれか（中身は戦いの後に受け取り）'],
          ['祠', '止まるとHPを回復'],
          ['番人', '敵本陣の前を守る強い妖（エリート・章ボス）。生きている間は本陣への攻撃が弱まり、本陣を殴ると目を覚まして追ってきます。倒すと本陣を割りやすくなります'],
          ['敵ヒーロー', '自分と同じように賽を振って動き、陣地を塗り、建物を建てて攻めてきます'],
        ]),
      ) + tip('赤い「！」のマスは大技の予告です。次の手番までに離れましょう。'),
  },
  {
    id: 'items',
    cat: 'battle',
    title: '道具',
    icon: 'potion',
    body: () =>
      p(`出撃前に<b>${ITEM_SLOTS}種類</b>選んで持っていきます。毎回その数だけ支給されるので、遠慮なく使ってください。使っても手番は終わりません（1手番に1回）。`) +
      table(itemRows()) +
      p('持っていく道具は、編成の「装備」タブで選べます。'),
  },
  {
    id: 'status',
    cat: 'battle',
    title: '状態異常',
    icon: 'drop',
    body: () =>
      p('確率ではかかりません。条件を満たせば必ずかかり、重ねがけもしません。駒の頭の上に字と残りの手番が出ます。') +
      table(statusRows()) +
      p('自分の<b>鳥居</b>のそばではかからず、手番の最初に治ります。本陣の前でも治ります。清め札でも治せます。'),
  },
  {
    id: 'defense',
    cat: 'battle',
    title: '防衛戦・敵の型',
    icon: 'shield',
    body: () =>
      h3('防衛戦') +
      p('「防衛」の札が付いた戦場は、敵の本陣がありません。数手番の準備のあと、妖の群れが波になって自分の本陣へ押し寄せます。最後の波まで本陣を守り切れば勝ち。') +
      p('準備のうちに野良の妖を減らし（生き残りは寝返って攻め手に加わります）、櫓・柵・落とし穴で道を固めましょう。建物は普段の2倍まで建てられます。') +
      h3('短期決戦（砦）') +
      p('「砦攻め」の札に砂時計の数字がある戦場は、その手番のうちに敵本陣を落とさないと負けです。戦いの最初にルールが出て、残り3手番でもう一度知らせます。戦闘中は残りの手番が敵本陣の横に出ます。') +
      h3('重装') +
      p('鎧を着た妖。鎧があるうちは物理の攻撃がほとんど通らず、当てるたびに1枚はがれます。<b>魔法（アカリの攻撃など）は鎧を無視</b>します。') +
      h3('攻城') +
      p('増援で現れ、ヒーローには目もくれず本陣へ向かう妖。左から出れば自分の本陣、右から出れば敵の本陣を狙います。'),
  },

  // ===== 育成 =====
  {
    id: 'grow-overview',
    cat: 'grow',
    title: '強くなるには',
    icon: 'enhance',
    body: () =>
      table([
        ['主Lv', '戦うと上がる、あなた自身のLv。上がるとスキルのポイントが増えます'],
        ['スキル', 'キャラごとに、ポイントを系統に振って強くする。振り直しは無料'],
        ['装備', '武器・防具・アクセサリー。戦いで拾い、銭で強化'],
        ['お守り', `1人${OMAMORI_SLOTS}つ。おまもりガチャや章ボスの初回・星箱・図鑑で手に入る`],
        ['式神', '倒した妖が仲間になる。持っているだけで全員が強くなり、式神召喚にセットすると戦いで技を使う'],
        ['建物図面', '戦闘で建てる建物の種類と強さ'],
        ['里', '施設で銭・材木などが時間で貯まる'],
      ]) +
      p('強さの目安は<b>戦力</b>。戦場ごとの<b>推奨戦力</b>と比べて、足りなければ育成しましょう。') +
      tip('まずは編成の「おすすめ装備」とスキルの「おすすめ」。拾った装備や新しいスキルを自動で組んでくれます。'),
  },
  {
    id: 'chara',
    cat: 'grow',
    title: '編成',
    icon: 'chara',
    body: () =>
      row(
        shot('chara', '編成：装備タブ', [
          [14, 13.4],
          [84, 8.7],
          [26, 72],
          [78, 72],
          [26, 82],
          [77, 82],
          [51, 89.5],
        ]),
        marks([
          '出撃するキャラ（顔を押して切り替え）',
          'おすすめ装備：戦力が一番上がる組み合わせに付け替え',
          '武器・防具・アクセサリー',
          '持っていく道具（2種類）',
          'お守り',
          '式神召喚（押して、戦いで呼ぶ式神を選ぶ）',
          'タブ：能力値／装備／スキル',
        ]),
      ) + goBtn('chara', '編成へ'),
  },
  {
    id: 'skills',
    cat: 'grow',
    title: 'スキルとポイント',
    icon: 'skill',
    body: () =>
      row(
        shot('skills', 'スキルのツリー'),
        p(`キャラごとに${LINE_COUNT}つの<b>系統</b>があり、<b>ポイント</b>を振って系統Lv（0〜${LINE_MAX}）を上げます。1Lvに1ポイント。振り直しは無料で、いつでも組み替えられます。`) +
          p(`系統Lvを上げるとその系統の能力が上がり、Lv${WAZA_LVS.slice(0, -1).join('・')}で技が開き、Lv${LINE_MAX}で極みに達します。術はその系統のLv${LINE_JUTSU_LV}で覚え、Lv${LINE_OUGI_LV}でその術の<b>奥義</b>を覚えます。`) +
          p('ポイントは主Lvで増えるほか、式神を集める・里の道場・外伝のクリアでも増えます。全部の系統を極めるほどは貯まらないので、どの系統を深くするかがビルドです。4人それぞれが満額使えます。') +
          p('迷ったら「おすすめ構成」を押すと、Lvが上がるたびに自動で組み直してくれます。ショップの「おたすけビルド」があれば、編成で型を選んでスキル・装備・お守り・道具・式神をまとめて組めます。'),
      ) + goBtn('skills', 'スキルへ'),
  },
  {
    id: 'equip',
    cat: 'grow',
    title: '装備',
    icon: 'equip',
    body: () =>
      row(
        shot('equip', '装備の詳細：強化・売却'),
        p('武器（キャラ専用）・防具・アクセサリーの3つ。戦いで拾い、レア度は<b>並・良・優・極</b>。極は上の難易度の章ボスなどからしか出ません。') +
          p('レア度が高いほど<b>銘</b>（追加の効果）が多く付きます。武器の「相性」の術をセットすると、固有の効果が強まります。') +
          p('<b>強化</b>：銭で+1ずつ。上限に届いたら、同じ装備（同じ名前でレア度が同じか上）を素材に<b>限界突破</b>で上限を延ばします（回数は里の鍛冶屋で増える）。') +
          p('要らない装備は売却か一括売却。<b>ロック</b>した装備は売れません。拾ったその場で売る「自動売却」も設定できます。'),
      ) + goBtn('enhance', '強化へ'),
  },
  {
    id: 'omamori',
    cat: 'grow',
    title: 'お守り',
    icon: 'omamori',
    body: () =>
      row(
        shot('omamori', 'お守りの一覧'),
        p(`キャラごとに<b>${OMAMORI_SLOTS}つ</b>付けられます。主効果と、レア度に応じた副効果（0〜3個）が付いています。`) +
          p(`主効果には${['sai2', 'nidobushin', 'kazaguruma'].map((id) => `「${esc(OMAMORI[id].rule)}」`).join('')}のように<b>戦い方が変わるもの</b>もあります。どれを付けるかで戦い方が変わります。`) +
          p('要らないお守り3つと銭で<b>錬成</b>すると、新しいお守りを1つ作れます（レア度が上がりやすい）。'),
      ) + goBtn('enhance', '強化へ'),
  },
  {
    id: 'shiki',
    cat: 'grow',
    title: '式神',
    icon: 'shiki',
    body: () =>
      row(
        shot('shiki', '式神の一覧'),
        p('式神になる妖は<b>10種族</b>。戦場で倒すと一定の確率で「仲間になりたそうにこちらを見ている」…仲間（式神）になります。') +
          p('式神は<b>持っているだけで4人全員</b>のHP・攻撃などが上がります。<b>式神Lv</b>は霊玉で上げ、<b>Lv15で★2・Lv30で★3</b>へ進化。姿が変わり、効果が伸びます。') +
          p('<b>式神召喚</b>：キャラごとに1体をセットすると、戦いには出ませんが、条件を満たしたとき自動でカットインして技で助けます（1戦に★の数まで）。3手番ごとの<b>加護</b>も重なっていきます。'),
      ) +
      p('地方の詳細（ⓘ）の「式神の候補」が、その国で仲間にできる式神です。'),
  },
  {
    id: 'blueprint',
    cat: 'grow',
    title: '建物図面',
    icon: 'blueprint',
    body: () =>
      row(
        shot('blueprint', '建物図面'),
        p('戦闘で建てる建物の図面。櫓・竹柵・幟は最初から建てられ、ほかの建物は図面を<b>習得</b>すると建てられるようになります。') +
          p('材木と銭でLvを上げると、建物のHPと効果が上がります。Lv5からは射程も伸びます。Lvは里の鍛冶屋のLvまで上げられます。全キャラ共通です。'),
      ),
  },
  {
    id: 'village',
    cat: 'grow',
    title: '里',
    icon: 'village',
    body: () =>
      row(
        shot('village', '里', [
          [51, 38],
          [73, 10],
          [30, 67],
          [28, 87],
          [72, 87],
        ]),
        marks(['施設。押すと下に情報が出ます', '米問屋・万屋の看板（看板娘の店。下の「米問屋・万屋」）', '選んだ施設の生産量と貯まり具合', '一括受取', '施設の強化（銭＋材木）']) +
          table((Object.values(VILLAGE) as { name: string; desc: string }[]).map((f): [string, string] => [esc(f.name), esc(f.desc)])),
      ) +
      p(`貯まるのは最大${M().village.capHours}時間分まで。施設のLvの上限は、国盗りで地方を制圧するほど上がります。`) +
      goBtn('village', '里へ'),
  },
  {
    id: 'shops',
    cat: 'grow',
    title: '米問屋・万屋',
    icon: 'shop',
    body: () =>
      row(
        shot('komedoiya', '米問屋', [
          [40, 25],
          [60, 68],
          [55, 89],
        ]),
        marks([
          '看板娘のこまち。押すと話す',
          `兵糧${M().komedoiya.hyoro}→掃討チケット${M().komedoiya.tickets}枚。枚数はスライダーと「最大」で（米問屋の通い帳が必要）`,
          '蔵出し：ショップで兵糧を買う。今日の分が無くなると売り切れ',
        ]),
      ) +
      row(
        shot('yorozuya', '万屋', [
          [40, 25],
          [50, 59],
          [50, 79],
        ]),
        marks(['看板娘のあかね。押すと話す', '品物と道具の切り替え', '品物（材木・ガチャ券・錬成札）は買うたびに値上がりし、日付が変わると戻る。道具は一度買えばずっと使える']),
      ) +
      goBtn('village', '里へ'),
  },

  // ===== 国盗り =====
  {
    id: 'map',
    cat: 'conquest',
    title: '国盗りの地図',
    icon: 'castle',
    body: () =>
      row(
        shot('map', '国盗りの地図', [
          [50, 9],
          [51, 47],
          [80, 82.3],
          [40, 88],
        ]),
        marks(['今の章と、制圧した国の数', '島の地図。攻め込める国が赤く光る', '色の意味：制圧・攻め込める・未踏・未解放', '選んだ地方。「攻める」で詳しく']) +
          p('国は<b>前線の地方</b>と<b>本城の地方</b>の2つでできています。') +
          steps(['前線の地方の合戦4つを勝つ（順番は自由）', '前線の<b>砦</b>を落とす', '本城の地方の合戦4つを勝つ', '<b>本城</b>を落とすと、その国を制圧。隣の国へ攻め込めるようになる']),
      ),
  },
  {
    id: 'sortie',
    cat: 'conquest',
    title: '出陣',
    icon: 'sortie',
    body: () =>
      row(
        shot('prov', '地方の詳細', [
          [51, 32],
          [70, 10],
          [34, 63],
          [51, 74],
          [11, 88.5],
          [30, 88],
          [44, 88],
          [73, 88],
        ]),
        marks([
          '地方の地図。戦場の位置と取った星',
          '難易度',
          '敵将と、国の制圧（地方の数）。ⓘで戦場の詳しい情報・式神の候補',
          '戦場の一覧：星・推奨戦力・兵糧',
          '出撃キャラと戦力（押して変更）',
          '掃討',
          'オート周回',
          '出陣（兵糧を使う）',
        ]) + p(`1戦に兵糧${M().stamina.costMin}。`),
      ),
  },
  {
    id: 'difficulty',
    cat: 'conquest',
    title: '難易度',
    icon: 'oni',
    body: () =>
      p('戦場には<b>通常・上</b>の難易度があります。本編（幽京城）をクリアすると上の難易度が開きます。上は第1章（青葉国）から1国ずつ、前の国の本城を上でクリアすると次の国が開きます（国の中の順番は通常と同じ）。') +
      p('上の敵は第1章から本編の終盤より強く、そのぶん報酬も多くなります。<b>極の装備</b>や業物（セット効果のある装備）は上の難易度でしか手に入りません。上の第100陣をクリアすると主Lvの上限が上がります。') +
      p('上の難易度の戦場には特別なマスがあります。<b>霊泉</b>＝手番の最初に回復、<b>神域</b>＝立っていると与ダメ上昇、<b>泥濘</b>＝通ると足止め、<b>毒沼</b>＝通ると毒。敵味方の両方に効きます。') +
      p('同じ戦場の2回目以降は、経験値・銭・材木・ドロップが少なくなります（初回は満額）。新しい戦場を優先して進めましょう。'),
  },
  {
    id: 'sweep',
    cat: 'conquest',
    title: '掃討',
    icon: 'sweep',
    body: () =>
      p(`<b>掃討</b>は、戦わずに勝った扱いで報酬を受け取る機能です。掃討チケット1枚で1戦（兵糧は使いません）。1回に${M().sweep.maxCount}戦まで。`) +
      p(`掃討チケットは毎日のログインで${M().sweep.ticketsDaily}枚、日替わり任務を全部達成すると${M().sweep.ticketsAllClear}枚。${M().sweep.ticketCap}枚まで持てます。`) +
      p(`クリア済みの戦場のほか、戦力が推奨の${M().sweep.unclearedPowerRatio}倍以上あれば未クリアの合戦も掃討できます（砦・本城は除く）。`),
  },
  {
    id: 'autorun',
    cat: 'conquest',
    title: 'オート周回',
    icon: 'auto',
    body: () =>
      row(
        shot('auto', 'オート周回の設定'),
        p('<b>Google連携</b>すると無料で使えます。地方の詳細の「オート」から始めます。兵糧は1戦ごとに使い、尽きたら回復を待って続けます。') +
          table([
            ['未クリアへ', '「挑戦する」なら未クリアの戦場へ順に進む。「しない」なら今の戦場を回り続ける'],
            ['負けたとき', '止める・再挑戦する・ひとつ手前に戻る'],
            ['終了', '何戦で止めるか'],
            ['戦い方', '「式神候補を必ず倒す」「龍脈をすべて取る（★3の条件）」を選べる。負けそうなときは勝つことを優先'],
            ['物語の場面を見る', '初めての物語の場面で止まって見る（切ると割り込まない）'],
          ]) +
          p(`1戦にかかる時間は決着までの手番数で決まり、無料では×${M().farm.speedFree}、倍速パックで×${M().farm.speedPaid}の速さ。`) +
          p(`画面を閉じても、<b>${M().farm.graceMinutes}分</b>までは次に開いたときにまとめて精算されます。「節電モード」にすると盤面を描かずに結果だけ表示します。`) +
          p('再挑戦は決めた回数続けて負けると、手前に戻るは手前でも負けが続くと止まります。戦闘画面の「このバトルで終わる」で、今の1戦が終わったら止められます。'),
      ),
  },
  {
    id: 'story',
    cat: 'conquest',
    title: '物語',
    icon: 'history',
    body: () =>
      p('国に入ったとき・砦を落としたとき・本城の前・国を制圧したときに、物語が始まります（初めてのときだけ）。オート周回では、設定の「物語の場面を見る」を入れたときだけ止まって見られます。'),
  },

  // ===== 資源・その他 =====
  {
    id: 'resources',
    cat: 'other',
    title: '資源',
    icon: 'coin',
    body: () =>
      table([
        [`${resIcon('zeni')}銭`, '装備の強化・里の施設・図面・万屋・錬成など、何にでも使うお金'],
        [`${resIcon('reidama')}霊玉`, '式神Lvを上げる。星の初回・章ボスの初回・図鑑・宝箱・里の祠・お守りの売却などで手に入る'],
        [`${resIcon('zaimoku')}材木`, '里の施設と建物図面に使う。戦闘・製材所で手に入る'],
        [`${resIcon('hyoro')}兵糧`, `出撃に使う（1戦${M().stamina.costMin}）。${M().stamina.regenMinutes}分に1回復。あふれた分は予備に貯まる`],
        [`${resIcon('kuji')}ガチャ券`, 'おまもりガチャを引く。ログボ・任務・星箱・万屋で手に入る'],
        [`${resIcon('soutou')}掃討チケット`, '掃討1戦に1枚。毎日のログインと任務で届く。米問屋の通い帳があれば兵糧と交換できる'],
        [`${icon('wall')}陣材`, '戦闘の中だけで使う、建物の材料'],
      ]) + p('霊玉・ガチャ券は売っていません。'),
  },
  {
    id: 'omikuji',
    cat: 'other',
    title: 'おまもりガチャ',
    icon: 'gacha',
    body: () =>
      row(
        shot('gacha', 'おまもりガチャ', [
          [78, 9.5],
          [64, 17],
          [51, 74.5],
          [51, 82.5],
          [15, 87.5],
          [78, 87.5],
        ]),
        marks(['持っているガチャ券', '今週のピックアップ（このお守りが出やすい）', '1日1回の無料ガチャ（券は使いません）', `1回（券${M().gacha.cost1}枚）・10連（券${M().gacha.cost10}枚、ランクA以上が1回確定）`, '排出率・抽選のしくみ', '極が確定するまでの回数']) +
          p(`お守りが当たります。極のお守りが出ないまま${M().gacha.pity}回引くと、極が確定します。`),
      ),
  },
  {
    id: 'missions',
    cat: 'other',
    title: '任務・ログインボーナス',
    icon: 'mission',
    body: () =>
      table([
        ['日替わり任務', '毎日0時（日本時間）に3つ入れ替わる。全部達成すると、ガチャ券と掃討チケット'],
        ['週替わり任務', '月曜0時に入れ替わる'],
        ['ログインボーナス', '7日で一周（連続でなくても進む）。毎日、掃討チケットも届く'],
        ['プレゼント', '運営からの贈り物。期限を過ぎると消えるので早めに受け取りを'],
      ]),
  },
  {
    id: 'social',
    cat: 'other',
    title: '番付・チャット',
    icon: 'rank',
    body: () =>
      table([
        ['番付', '国盗りは制圧した国の数 → 星の合計の順（同じなら同順位）。ほかに主Lvの順位。10分ごとに更新'],
        ['図鑑', '出会った妖の記録。新しい妖に出会うと霊玉がもらえます'],
        ['チャット', '全体チャット。困ったことを聞いてみるのも◎'],
        ['報告', '不具合や感想は、チャットの入力欄の左の「報告」から。端末や画面の情報も自動で添えられます'],
      ]),
  },
  {
    id: 'shop',
    cat: 'other',
    title: 'ショップ',
    icon: 'shop',
    body: () =>
      p('育成を早めるものと手間を省くものです。霊玉は売りません。') +
      // 条件つき（限定）の商品は期間・人で変わるので載せない
      table(PRODUCTS.filter((x) => !x.offer).map((x): [string, string] => [esc(x.name), `${fmt(x.yen)}円`])),
  },
];
